import { useEffect, useState } from "react";
import { api } from "../../api/client";
import TodoListPanel from "./TodoListPanel";
import { Button, Icon, Spinner, cx,
  ModalOverlay
} from "../../components/ui";
import { getISODateParts, getTodayISOInJakarta } from "../../utils/date";
import {
  LATE_MAX_MIN,
  ONTIME_MAX_MIN,
  PUNCTUALITY,
  PUNCTUALITY_STYLES,
  punctualityGuide
} from "../../utils/attendance";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const BLN_PENDEK = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
// "2026-10-31" → "31 Okt 2026"
function tanggalBaca(iso) {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  return y && m && d ? `${d} ${BLN_PENDEK[m - 1]} ${y}` : String(iso || "");
}

function todayISO() {
  return getTodayISOInJakarta();
}

// Tanggal yang sedang dibuka, dibawa melintasi pergantian cabang: tombol "Buka BRxx" di
// pemberitahuan kelas pengganti memuat ulang halaman ini di cabang lain, dan trainer tak
// perlu memilih tanggal (lampau) itu lagi.
const TANGGAL_DIBAWA = "kip_absensi_tanggal";
function tanggalAwal() {
  try {
    const t = sessionStorage.getItem(TANGGAL_DIBAWA);
    if (t && /^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  } catch {
    /* abaikan */
  }
  return todayISO();
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AttendancePage({ user, setToast, onBukaCabang }) {
  // attendanceState: 'SELECT_SESSION' | 'LOCKED_SESSION' | 'ACTIVE_ATTENDANCE'
  const [attendanceState, setAttendanceState] = useState("SELECT_SESSION");

  const [selectedDate, setSelectedDate] = useState(tanggalAwal);
  useEffect(() => {
    try { sessionStorage.removeItem(TANGGAL_DIBAWA); } catch { /* abaikan */ }
  }, []);
  const [sessions, setSessions] = useState([]);
  // Kelas yang ia gantikan di cabang LAIN pada tanggal ini (kartunya hanya ada di cabang itu).
  const [coverLain, setCoverLain] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  // Payroll bulan tanggal ini sudah dikunci admin → tak ada yang bisa diubah.
  const [payrollTerkunci, setPayrollTerkunci] = useState(false);

  // Locked session metadata
  const [lockedSession, setLockedSession] = useState(null);

  // Kanban state
  const [pending, setPending] = useState([]);
  const [checked, setChecked] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Tambah murid dari kelas lain
  const [addOpen, setAddOpen] = useState(false);
  const [allStudents, setAllStudents] = useState([]);

  // Masa pengawasan pada tanggal yang dibuka. null = sedang dimuat; { aktif: null } = tak
  // diketahui (galat). Yang tak diketahui TIDAK dikunci — server tetap menjaga — tapi panel
  // to-do tetap ditampilkan, supaya trainer yang ternyata diawasi tak tertahan tanpa
  // formulir untuk membukanya.
  const [pengawasan, setPengawasan] = useState(null);
  useEffect(() => {
    let batal = false;
    setPengawasan(null);
    Promise.resolve(api.getPengawasanSaya(selectedDate))
      .then((r) => { if (!batal) setPengawasan(r && r.success ? r : { aktif: null }); })
      .catch(() => { if (!batal) setPengawasan({ aktif: null }); });
    return () => { batal = true; };
  }, [selectedDate]);

  // ── Load sessions whenever date changes ──
  async function loadSessions(date) {
    setLoadingSessions(true);
    setSessions([]);
    setCoverLain([]);
    setPayrollTerkunci(false);
    try {
      const res = await api.getAttendanceSessions({
        userEmail: user.email,
        date
      });
      if (res.success) {
        setSessions(res.sessions || []);
        setCoverLain(Array.isArray(res.coverLain) ? res.coverLain : []);
        setPayrollTerkunci(!!res.payrollTerkunci);
      }
      else setToast({ type: "error", message: res.message });
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setLoadingSessions(false);
    }
  }

  useEffect(() => {
    if (attendanceState === "SELECT_SESSION") {
      loadSessions(selectedDate);
    }
  }, [selectedDate, attendanceState]);

  // ── Pindah ke cabang tempat kelas penggantinya berada, membawa tanggal ini ──
  // Yang dikirim ke App berbentuk CABANG ({ id, code, name }), bukan baris coverLain.
  function bukaCabangLain(c) {
    try { sessionStorage.setItem(TANGGAL_DIBAWA, selectedDate); } catch { /* abaikan */ }
    onBukaCabang({ id: c.branchId, code: c.code, name: c.name });
  }

  // ── Phase: SELECT_SESSION → LOCKED_SESSION ──
  function startSession(sessionData) {
    if (sessionData.locked || sessionData.coveredByOther || payrollTerkunci) return; // terkunci / dikover
    setLockedSession(sessionData);
    setPending(sessionData.pending || []);
    setChecked(sessionData.checked || []);
    setAttendanceState("LOCKED_SESSION");
  }

  // ── Minta izin edit ke admin (sesi yang terkunci) ──
  async function requestEdit(session) {
    const reason = window.prompt("Alasan minta edit absensi (opsional):");
    if (reason === null) return; // dibatalkan

    try {
      const dateParts = getISODateParts(selectedDate);
      const res = await api.createEditRequest({
        year: dateParts.year,
        month: dateParts.monthName,
        day: dateParts.day,
        session: session.session,
        trainer: user.nickname || user.name,
        reason
      });
      setToast({ type: res.success ? "success" : "error", message: res.message });
      if (res.success) loadSessions(selectedDate);
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  }

  // ── Phase: LOCKED_SESSION → ACTIVE_ATTENDANCE ──
  function beginClass() {
    setAttendanceState("ACTIVE_ATTENDANCE");
  }

  // ── Tandai Hadir sekaligus keterangan kedatangannya (On Time / Late / Very Late) ──
  // Jam tombol ditekan tetap dicatat server sebagai jejak, tapi tak lagi ditampilkan.
  async function markStudent(student, punctuality) {
    // actualTrainer = siapa yang sedang login dan mengisi presensi
    const actualTrainer = user.nickname || user.name;
    const status = "Attend";

    // Optimistic update — tampilkan cover indicator jika beda trainer
    setPending((prev) => prev.filter((s) => s.studentId !== student.studentId));
    const displayStatus =
      String(student.trainer || "").toLowerCase() !== String(actualTrainer || "").toLowerCase()
        ? `${status}:${actualTrainer}`
        : status;
    setChecked((prev) => [...prev, { ...student, status: displayStatus, punctuality }]);

    try {
      const dateParts = getISODateParts(selectedDate);
      const res = await api.submitAttendanceRecord({
        year: dateParts.year,
        month: dateParts.monthName,
        day: dateParts.day,
        studentId: student.studentId,
        name: student.name,
        trainer: student.trainer,       // trainer ASLI dari siswa (pemilik kelas)
        actualTrainer,                  // trainer yang SEDANG mengisi (pengganti / cover)
        sessions: lockedSession.session,
        status,
        punctuality
      });
      setToast({
        type: res.success ? "success" : "error",
        message: res.message || "Attendance saved"
      });
      if (!res.success) refreshSession();
    } catch (err) {
      setToast({ type: "error", message: err.message });
      refreshSession();
    }
  }

  // ── Koreksi keterangan kedatangan murid yang sudah hadir ──
  // Ditolak server (mis. hari sudah terkunci) → kartu diambil ulang, supaya pilihan yang
  // tampil kembali ke yang benar-benar tersimpan.
  async function editPunctuality(student, punctuality) {
    setChecked((prev) =>
      prev.map((s) => (s.studentId === student.studentId ? { ...s, punctuality } : s))
    );

    try {
      const dateParts = getISODateParts(selectedDate);
      const res = await api.updatePunctuality({
        year: dateParts.year,
        month: dateParts.monthName,
        day: dateParts.day,
        studentId: student.studentId,
        sessions: lockedSession.session,
        actualTrainer: user.nickname || user.name,
        punctuality
      });
      if (!res.success) {
        setToast({ type: "error", message: res.message || "Gagal memperbarui keterangan" });
        refreshSession();
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
      refreshSession();
    }
  }

  // Ambil ulang sesi aktif dari server (untuk sinkron additions/status). Juga dipakai saat
  // Tandai/Undo ditolak: data di layar bisa basi — mis. murid dua kelas yang barusan diabsen
  // trainer lain — dan memulihkan dari daftar lama hanya mengembalikan keadaan yang salah.
  async function refreshSession() {
    if (!lockedSession) return;
    try {
      const res = await api.getAttendanceSessions({
        userEmail: user.email,
        date: selectedDate
      });
      const fresh = (res.sessions || []).find((s) => s.session === lockedSession.session);
      if (fresh) {
        setLockedSession(fresh);
        setPending(fresh.pending || []);
        setChecked(fresh.checked || []);
      }
    } catch {
      /* abaikan */
    }
  }

  // ── Batalkan / undo penambahan murid ──
  // Pending → batalkan biasa. Approved → beri peringatan (sudah dihitung gaji).
  async function cancelAddition(addition) {
    const approved = addition.additionStatus === "approved";
    if (approved) {
      const ok = window.confirm(
        `Murid tambahan "${addition.name}" sudah DISETUJUI admin dan sudah dihitung ke gaji Anda.\n\n` +
          `Membatalkannya akan menghapus murid ini dari kelas dan menghitung ulang gaji Anda. Lanjutkan?`
      );
      if (!ok) return;
    }
    try {
      const res = await api.cancelStudentAddition({
        id: addition.additionId,
        trainer: user.nickname || user.name
      });
      setToast({ type: res.success ? "success" : "error", message: res.message });
      if (res.success) refreshSession();
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  }

  // ── Koreksi keterangan kedatangan murid TAMBAHAN (disimpan di baris penambahannya) ──
  async function editAdditionPunctuality(addition, punctuality) {
    setLockedSession((prev) => prev && {
      ...prev,
      additions: (prev.additions || []).map((a) =>
        a.additionId === addition.additionId ? { ...a, punctuality } : a)
    });
    try {
      const res = await api.updateAdditionPunctuality({
        id: addition.additionId,
        trainer: user.nickname || user.name,
        punctuality
      });
      if (!res.success) {
        setToast({ type: "error", message: res.message || "Gagal memperbarui keterangan" });
        refreshSession();
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
      refreshSession();
    }
  }

  // ── Buka modal tambah murid (lazy-load daftar murid) ──
  async function openAddModal() {
    setAddOpen(true);
    if (allStudents.length === 0) {
      try {
        const res = await api.getAllStudents();
        if (res.success) setAllStudents(res.data || []);
      } catch {
        /* abaikan */
      }
    }
  }

  // ── Kirim permintaan tambah murid ke admin ──
  async function addStudent(studentId, reason, punctuality) {
    try {
      const dateParts = getISODateParts(selectedDate);
      const res = await api.addStudentToSession({
        year: dateParts.year,
        month: dateParts.monthName,
        day: dateParts.day,
        session: lockedSession.session,
        trainer: user.nickname || user.name,
        studentId,
        reason,
        punctuality
      });
      setToast({ type: res.success ? "success" : "error", message: res.message });
      if (res.success) {
        setAddOpen(false);
        refreshSession();
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  }

  // ── Undo: batalkan kehadiran (hapus dari DB juga) ──
  async function undoStudent(student) {
    // Optimistic: pindahkan kembali ke "Belum Hadir"
    setChecked((prev) => prev.filter((s) => s.studentId !== student.studentId));
    setPending((prev) => [...prev, { ...student, status: undefined, punctuality: undefined }]);

    try {
      const dateParts = getISODateParts(selectedDate);
      const res = await api.unmarkAttendance({
        year: dateParts.year,
        month: dateParts.monthName,
        day: dateParts.day,
        studentId: student.studentId,
        sessions: lockedSession.session,
        actualTrainer: user.nickname || user.name
      });
      if (!res.success) {
        setToast({ type: "error", message: res.message || "Gagal membatalkan kehadiran" });
        refreshSession();
      }
    } catch (err) {
      setToast({ type: "error", message: err.message });
      refreshSession();
    }
  }

  // ── Submit entire session ──
  // zeroAttendance=true → kelas berjalan tanpa murid (0 hadir), gaji tarif terendah.
  async function submitSession(zeroAttendance = false) {
    if (zeroAttendance) {
      const ok = window.confirm(
        "Kirim permintaan 'Selesai Tanpa Murid' ke admin? Kelas dihitung ke gaji (tarif terendah) SETELAH admin menyetujui."
      );
      if (!ok) return;
    } else if (pending.length > 0) {
      // Murid yang tidak ditandai hadir dianggap tidak datang — boleh diselesaikan
      // tanpa menandai semua, cukup konfirmasi bila masih ada yang tersisa.
      const ok = window.confirm(
        `${pending.length} murid belum ditandai hadir dan tidak akan tercatat datang. Selesaikan sesi ini?`
      );
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      const dateParts = getISODateParts(selectedDate);
      const res = await api.completeSession({
        year: dateParts.year,
        month: dateParts.monthName,
        day: dateParts.day,
        session: lockedSession.session,
        trainer: user.nickname || user.name,
        zeroAttendance,
        // Kelas di kartu ini: server memakainya untuk memeriksa to-do wajib masa pengawasan
        // pada kelas yang SAMA dengan yang dilihat trainer.
        classIds: lockedSession.classIds || []
      });
      setToast({
        type: res?.success === false ? "error" : "success",
        message: res?.message || "Sesi presensi berhasil diselesaikan!"
      });
      setAttendanceState("SELECT_SESSION");
      setLockedSession(null);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal menandai selesai" });
    } finally {
      setSubmitting(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {pengawasan?.aktif === true && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <p className="flex items-center gap-2 text-sm font-black">
            <Icon name="visibility" />
            Kamu dalam masa pengawasan sampai {tanggalBaca(pengawasan.selesai)}.
          </p>
          <p className="mt-1 text-xs leading-relaxed">
            To-Do List wajib diisi di setiap kelas yang kamu ajar. Tombol Simpan absensi terbuka
            setelah ketujuh tahap berisi aktivitas — dari daftar atau tulisan sendiri.
            &quot;Selesai Tanpa Murid&quot; tidak terkunci.
          </p>
        </div>
      )}

      {/* ── Phase: SELECT_SESSION ── */}
      {attendanceState === "SELECT_SESSION" && (
        <SelectSessionPhase
          user={user}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          sessions={sessions}
          loading={loadingSessions}
          onSelectSession={startSession}
          onRequestEdit={requestEdit}
          payrollTerkunci={payrollTerkunci}
          coverLain={coverLain}
          onBukaCabang={onBukaCabang ? bukaCabangLain : null}
        />
      )}

      {/* ── Phase: LOCKED_SESSION ── */}
      {attendanceState === "LOCKED_SESSION" && lockedSession && (
        <LockedSessionPhase
          lockedSession={lockedSession}
          selectedDate={selectedDate}
          pending={pending}
          checked={checked}
          onBeginClass={beginClass}
          onBack={() => setAttendanceState("SELECT_SESSION")}
        />
      )}

      {/* ── Phase: ACTIVE_ATTENDANCE ── */}
      {attendanceState === "ACTIVE_ATTENDANCE" && lockedSession && (
        <ActiveAttendancePhase
          lockedSession={lockedSession}
          selectedDate={selectedDate}
          pending={pending}
          checked={checked}
          submitting={submitting}
          onMark={markStudent}
          onUndo={undoStudent}
          onEditPunctuality={editPunctuality}
          onCancelAddition={cancelAddition}
          onEditAdditionPunctuality={editAdditionPunctuality}
          onAddStudent={openAddModal}
          onSubmit={submitSession}
          setToast={setToast}
          pengawasan={pengawasan}
        />
      )}

      <AddStudentModal
        open={addOpen}
        students={allStudents}
        session={lockedSession?.session}
        existingIds={
          new Set(
            [
              ...pending,
              ...checked,
              ...(lockedSession?.additions || [])
            ].map((s) => String(s.studentId))
          )
        }
        onClose={() => setAddOpen(false)}
        onSubmit={addStudent}
      />
    </div>
  );
}

// ─── Phase A: SELECT_SESSION ──────────────────────────────────────────────────
export function SelectSessionPhase({
  user,
  selectedDate,
  setSelectedDate,
  sessions,
  loading,
  onSelectSession,
  onRequestEdit,
  payrollTerkunci = false,
  coverLain = [],
  onBukaCabang = null
}) {
  // Separate covered sessions from native sessions
  const coveredSessions = sessions.filter((s) =>
    s.pending?.some((st) => st.isCovered) || s.checked?.some((st) => st.isCovered)
  );
  const hasCoverage = coveredSessions.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-black tracking-tight">Daily Attendance</h1>
        <p className="mt-1 text-sm text-slate-500">Pilih tanggal untuk memuat sesi kelas Anda.</p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div>
          <label htmlFor="absensi-tanggal" className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Tanggal Kelas
          </label>
          <input
            id="absensi-tanggal"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>
      </div>

      {/* Payroll bulan ini sudah dikunci: kartu-kartunya tak menawarkan tombol apa
          pun, dan inilah alasannya — supaya trainer tak mengira halamannya rusak. */}
      {!loading && payrollTerkunci && (
        <div className="rounded-2xl border border-slate-300 bg-slate-100 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
          <p className="flex items-center gap-2 text-sm font-black">
            <Icon name="lock" className="text-base" />
            Payroll bulan ini sudah dikunci admin
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Absensinya tak bisa diubah lagi. Kalau ada yang keliru, hubungi admin cabang.
          </p>
        </div>
      )}

      {/* Kelas pengganti di cabang LAIN: kartunya hanya ada di cabang kelas itu, jadi
          tunjukkan di mana — tanpa ini pengganti lintas cabang tak tahu ada yang menunggu. */}
      {!loading && coverLain.length > 0 && (
        <section
          aria-label="Kelas pengganti di cabang lain"
          className="rounded-2xl border border-purple-200 bg-purple-50 px-4 py-3 dark:border-purple-800/40 dark:bg-purple-900/20"
        >
          <p className="flex items-center gap-2 text-sm font-black text-purple-800 dark:text-purple-200">
            <Icon name="swap_horiz" className="text-xl text-purple-500" />
            Kelas pengganti di cabang lain
          </p>
          <ul className="mt-2 space-y-2">
            {coverLain.map((c) => (
              <li key={`${c.branchId}|${c.session}|${c.originalTrainer}`} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-purple-900 dark:text-purple-100">
                  Menggantikan <b>{c.originalTrainer}</b> · {c.session} di <b>{c.code}</b>
                  {c.name ? ` · ${c.name}` : ""}
                </span>
                {onBukaCabang && (
                  <Button className="bg-purple-600 hover:bg-purple-500" onClick={() => onBukaCabang(c)}>
                    <Icon name="store" />
                    Buka {c.code}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Coverage notice banner */}
      {!loading && hasCoverage && (
        <div className="rounded-2xl border border-purple-200 bg-purple-50 px-4 py-3 dark:border-purple-800/40 dark:bg-purple-900/20">
          <div className="flex items-center gap-2">
            <Icon name="swap_horiz" className="text-purple-500 text-xl" />
            <div>
              <p className="font-black text-purple-800 dark:text-purple-200 text-sm">
                Anda memiliki {coveredSessions.length} kelas penggantian (coverage) hari ini.
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-300 mt-0.5">
                Kelas ini bukan kelas asli Anda — absensi yang Anda isi akan dihitung ke gaji Anda.
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <Spinner label="Memuat sesi…" />
      ) : sessions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-16 text-center text-slate-500 dark:border-slate-700">
          <Icon name="event_busy" className="block text-5xl mb-3 text-slate-300" />
          Tidak ada sesi terjadwal untuk tanggal ini.
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {sessions.map((sess) => (
            <SessionCard
              key={sess.session}
              session={sess}
              onSelect={onSelectSession}
              onRequestEdit={onRequestEdit}
              payrollTerkunci={payrollTerkunci}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function SessionCard({ session, onSelect, onRequestEdit, payrollTerkunci = false }) {
  const total = session.totalStudents;
  const doneCount = session.checked?.length || 0;
  const isDone = total > 0 && doneCount >= total;
  const submitted = !!session.submitted;
  const zeroPending = !!session.zeroPending;
  const locked = !!session.locked;
  const requestStatus = session.requestStatus || "none";
  const coveredByOther = !!session.coveredByOther;
  const coverTrainer = session.coverTrainer || "";

  // Detect if this is a coverage session (all or some students are covered)
  const allStudents = [...(session.pending || []), ...(session.checked || [])];
  const isCoveredSession = allStudents.some((s) => s.isCovered);
  const isRescheduledSession = allStudents.some((s) => s.isRescheduled);

  // Get original trainer name from first covered student
  const coveredStudent = allStudents.find((s) => s.isCovered);
  const originalTrainerName = coveredStudent?.trainer || null;

  return (
    <div className={cx(
      "flex flex-col rounded-2xl border bg-white shadow-lg dark:bg-slate-900/60",
      isCoveredSession
        ? "border-purple-200 dark:border-purple-800/50"
        : "border-slate-200 dark:border-slate-800"
    )}>
      {/* Top accent bar */}
      <div className={cx(
        "h-1.5 w-full rounded-t-2xl bg-gradient-to-r",
        isCoveredSession ? "from-purple-400 to-violet-600" : "from-primary to-blue-500"
      )} />

      <div className="flex-1 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xl font-black">{session.session}</p>
          <span className={cx(
            "rounded-full px-3 py-1 text-xs font-black",
            locked
              ? "bg-slate-400/15 text-slate-500"
              : zeroPending
              ? "bg-amber-500/15 text-amber-600"
              : submitted
              ? "bg-green-500/15 text-green-600"
              : "bg-primary/10 text-primary"
          )}>
            {locked
              ? "🔒 Terkunci"
              : zeroPending
              ? "⏳ Menunggu Persetujuan"
              : submitted
              ? "Selesai"
              : session.period}
          </span>
        </div>

        {/* Coverage / Reschedule badges */}
        <div className="flex flex-wrap gap-1.5">
          {coveredByOther && (
            <div className="flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 dark:border-rose-700/40 dark:bg-rose-900/30">
              <Icon name="swap_horiz" className="text-rose-500 text-sm" />
              <span className="text-xs font-black text-rose-700 dark:text-rose-300">
                Dikover oleh {coverTrainer}
              </span>
            </div>
          )}
          {isCoveredSession && (
            <div className="flex items-center gap-1 rounded-lg border border-purple-200 bg-purple-50 px-2.5 py-1 dark:border-purple-700/40 dark:bg-purple-900/30">
              <Icon name="swap_horiz" className="text-purple-500 text-sm" />
              <span className="text-xs font-black text-purple-700 dark:text-purple-300">
                Cover: gantikan {originalTrainerName}
              </span>
            </div>
          )}
          {isRescheduledSession && (
            <div className="flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 dark:border-amber-700/40 dark:bg-amber-900/30">
              <Icon name="event_repeat" className="text-amber-500 text-sm" />
              <span className="text-xs font-black text-amber-700 dark:text-amber-300">
                Ada murid reschedule
              </span>
            </div>
          )}
          {!coveredByOther && session.ownCoveredBy && (
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 dark:border-slate-700 dark:bg-slate-800/40">
              <Icon name="info" className="text-slate-400 text-sm" />
              <span className="text-xs font-black text-slate-500 dark:text-slate-400">
                Kelas asli Anda diajar {session.ownCoveredBy}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
          <span className="flex items-center gap-1">
            <Icon name="group" className="text-base" />
            {total} murid
          </span>
          <span className="flex items-center gap-1">
            <Icon name="task_alt" className="text-base text-green-500" />
            {doneCount} tercatat
          </span>
          {(session.additions?.length || 0) > 0 && (
            <span className="flex items-center gap-1 text-purple-500">
              <Icon name="person_add" className="text-base" />
              {session.additions.length} tambahan
            </span>
          )}
        </div>

        <div className="space-y-1">
          <div className="flex justify-between text-xs text-slate-400">
            <span>{doneCount}/{total} recorded</span>
            <span>{total > 0 ? Math.round((doneCount / total) * 100) : 0}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className={cx("h-full rounded-full", isDone ? "bg-green-500" : isCoveredSession ? "bg-purple-500" : "bg-primary")}
              style={{ width: total > 0 ? `${(doneCount / total) * 100}%` : "0%" }}
            />
          </div>
        </div>
      </div>

      <div className={cx(
        "border-t p-4",
        isCoveredSession ? "border-purple-100 dark:border-purple-900/30" : "border-slate-100 dark:border-slate-800"
      )}>
        {coveredByOther ? (
          <button
            disabled
            className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-rose-100 px-4 py-2.5 text-sm font-black text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
          >
            <Icon name="swap_horiz" />
            Dikover oleh {coverTrainer}
          </button>
        ) : payrollTerkunci ? (
          // Bukan "Minta Edit": permintaannya pasti ditolak selama bulannya terkunci.
          <button
            disabled
            className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-black text-slate-500 dark:bg-slate-800 dark:text-slate-400"
          >
            <Icon name="lock" />
            Payroll bulan ini sudah dikunci
          </button>
        ) : locked ? (
          requestStatus === "pending" ? (
            <button
              disabled
              className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-amber-100 px-4 py-2.5 text-sm font-black text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
            >
              <Icon name="hourglass_top" />
              Menunggu Persetujuan Admin
            </button>
          ) : (
            <button
              onClick={() => onRequestEdit(session)}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-black text-slate-600 transition hover:border-primary hover:text-primary dark:border-slate-600 dark:text-slate-300"
            >
              <Icon name="lock" />
              {requestStatus === "rejected" ? "Ditolak — Minta Lagi" : "Minta Edit ke Admin"}
            </button>
          )
        ) : zeroPending ? (
          <button
            disabled
            className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-amber-100 px-4 py-2.5 text-sm font-black text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          >
            <Icon name="hourglass_top" />
            Menunggu Persetujuan Admin
          </button>
        ) : submitted ? (
          <button
            onClick={() => onSelect(session)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-yellow-400 px-4 py-2.5 text-sm font-black text-yellow-900 hover:bg-yellow-300 transition"
          >
            <Icon name="edit_note" />
            Edit Kelas
          </button>
        ) : (
          <Button
            className={cx("w-full", isCoveredSession && "bg-purple-600 hover:bg-purple-500")}
            onClick={() => onSelect(session)}
          >
            <Icon name="play_arrow" />
            Mulai Kelas {isCoveredSession ? "(Coverage)" : ""}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Phase B: LOCKED_SESSION ──────────────────────────────────────────────────
function LockedSessionPhase({ lockedSession, selectedDate, pending, checked, onBeginClass, onBack }) {
  const allStudents = [...pending, ...checked];
  const submitted = !!lockedSession.submitted;
  const isCoveredSession = allStudents.some((s) => s.isCovered);
  const coveredStudent = allStudents.find((s) => s.isCovered);
  const originalTrainerName = coveredStudent?.trainer || null;

  return (
    <div className="relative">
      {/* Background preview (blurred) */}
      <div className="pointer-events-none select-none opacity-30 blur-sm space-y-4">
        <div className="h-12 rounded-xl bg-slate-200 dark:bg-slate-700 w-1/3" />
        <div className="grid gap-4 xl:grid-cols-2">
          {[...pending, ...checked].slice(0, 4).map((s, i) => (
            <div key={i} className="h-16 rounded-xl bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white/95 p-8 shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95 text-center space-y-5">
          <div className={cx(
            "mx-auto flex h-16 w-16 items-center justify-center rounded-2xl",
            isCoveredSession ? "bg-purple-500/15 text-purple-600" : "bg-primary/15 text-primary"
          )}>
            <Icon name={isCoveredSession ? "swap_horiz" : "lock_clock"} className="text-3xl" />
          </div>

          <div>
            <h2 className="text-2xl font-black">{lockedSession.session}</h2>
            <p className="mt-1 text-sm text-slate-500">{selectedDate}</p>
            {isCoveredSession && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 dark:border-purple-700/40 dark:bg-purple-900/30">
                <Icon name="swap_horiz" className="text-purple-500 text-sm" />
                <span className="text-xs font-black text-purple-700 dark:text-purple-300">
                  Kelas Coverage — menggantikan {originalTrainerName}
                </span>
              </div>
            )}
          </div>

          <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-4 text-left space-y-2">
            <p className="text-xs font-bold uppercase text-slate-400">Ringkasan Sesi</p>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-300">Total Murid</span>
              <span className="font-black">{lockedSession.totalStudents}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-300">Belum Dicatat</span>
              <span className="font-black text-amber-500">{pending.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-300">Sudah Tercatat</span>
              <span className="font-black text-green-500">{checked.length}</span>
            </div>
          </div>

          {isCoveredSession && (
            <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-xs text-purple-800 dark:border-purple-700/40 dark:bg-purple-900/20 dark:text-purple-300 text-left">
              <p className="font-black mb-1">ℹ️ Kelas ini adalah kelas coverage</p>
              <p>Absensi yang Anda isi akan dihitung ke akumulasi gaji Anda secara otomatis.</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onBack}
              className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:border-primary hover:text-primary dark:border-slate-700 dark:text-slate-300 transition"
            >
              ← Kembali
            </button>
            <Button className={cx("flex-1", isCoveredSession && "bg-purple-600 hover:bg-purple-500")} onClick={onBeginClass}>
              <Icon name={submitted ? "edit_note" : "play_circle"} />
              {submitted ? "Edit Kelas" : "Mulai Kelas"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Kelompokkan murid per asal kelas: kelas sendiri vs kelas cover (per trainer asal).
function groupStudents(list) {
  const groups = new Map();
  list.forEach((s) => {
    const own = !s.isCovered;
    const key = own ? "__own__" : `cover:${s.trainer}:${s.coverageType || "substitution"}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        own,
        trainer: s.trainer,
        coverageType: s.coverageType || "substitution",
        items: []
      });
    }
    groups.get(key).items.push(s);
  });
  // Kelas sendiri dulu, lalu kelas-kelas cover
  return [...groups.values()].sort((a, b) => (a.own === b.own ? 0 : a.own ? -1 : 1));
}

function GroupHeader({ group }) {
  if (group.own) {
    return (
      <p className="pt-1 text-[11px] font-black uppercase tracking-wide text-slate-500">
        Kelas Anda
      </p>
    );
  }
  const merge = group.coverageType === "merge";
  return (
    <p
      className={cx(
        "flex items-center gap-1.5 pt-1 text-[11px] font-black uppercase tracking-wide",
        merge ? "text-blue-500" : "text-purple-500"
      )}
    >
      <Icon name={merge ? "merge_type" : "swap_horiz"} className="text-sm" />
      Kelas {group.trainer || "?"} · {merge ? "Gabung" : "Pengganti"}
    </p>
  );
}

// ─── Phase C: ACTIVE_ATTENDANCE ───────────────────────────────────────────────
function ActiveAttendancePhase({
  lockedSession,
  selectedDate,
  pending,
  checked,
  submitting,
  onMark,
  onUndo,
  onEditPunctuality,
  onCancelAddition,
  onEditAdditionPunctuality,
  onAddStudent,
  onSubmit,
  setToast,
  pengawasan = null
}) {
  const additions = lockedSession.additions || [];
  // Murid yang ada di dua kelas berjam sama dan hari ini sudah diabsen trainer lain:
  // hanya diberitahukan, tanpa tombol — sel absennya milik trainer itu.
  const diKelasLain = lockedSession.diKelasLain || [];

  // ── To-Do List wajib (masa pengawasan) ──
  // Dikunci HANYA bila jelas: trainer diawasi DAN panel melaporkan kelasnya wajib diisi DAN
  // to-do yang tersimpan belum lengkap. Keadaan yang tak diketahui tak mengunci — server
  // tetap memeriksa, dan penolakannya muncul sebagai pesan.
  const [todoStatus, setTodoStatus] = useState({ berlaku: null, tahapTersimpan: 0 });
  const wajibTodo = pengawasan?.aktif === true;
  const tahapWajib = pengawasan?.tahapWajib || 7;
  const tampilTodo = !!pengawasan && pengawasan.aktif !== false;
  const terkunciTodo = wajibTodo && todoStatus.berlaku === true && todoStatus.tahapTersimpan < tahapWajib;
  const allStudents = [...pending, ...checked];
  const isCoveredSession = allStudents.some((s) => s.isCovered);
  const coveredStudent = allStudents.find((s) => s.isCovered);
  const originalTrainerName = coveredStudent?.trainer || null;
  // Tampilkan grup berlabel hanya bila ada murid cover (kelas gabungan / pengganti).
  const showGroups = isCoveredSession;

  function renderPending(list) {
    if (!showGroups) {
      return list.map((s) => <PendingRow key={s.studentId} student={s} onMark={onMark} />);
    }
    return groupStudents(list).map((g) => (
      <div key={g.key} className="space-y-3">
        <GroupHeader group={g} />
        {g.items.map((s) => (
          <PendingRow key={s.studentId} student={s} onMark={onMark} />
        ))}
      </div>
    ));
  }

  function renderChecked(list) {
    const row = (s) => (
      <CheckedRow
        key={s.studentId}
        student={s}
        onUndo={onUndo}
        onEditPunctuality={onEditPunctuality}
      />
    );
    if (!showGroups) return list.map(row);
    return groupStudents(list).map((g) => (
      <div key={g.key} className="space-y-3">
        <GroupHeader group={g} />
        {g.items.map(row)}
      </div>
    ));
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-black">{lockedSession.session}</h1>
            {isCoveredSession && (
              <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2.5 py-1 text-xs font-black text-purple-700 dark:border-purple-700/40 dark:bg-purple-900/30 dark:text-purple-300">
                <Icon name="swap_horiz" className="text-sm" />
                Coverage
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-slate-500">
            {selectedDate} · {lockedSession.totalStudents} murid
            {isCoveredSession && ` · Menggantikan ${originalTrainerName}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onAddStudent}
            className="flex items-center gap-2 rounded-xl border border-purple-300 px-4 py-2.5 text-sm font-black text-purple-600 transition hover:bg-purple-50 dark:border-purple-700/50 dark:hover:bg-purple-900/20"
          >
            <Icon name="person_add" />
            Tambah Murid
          </button>
          {checked.length === 0 ? (
            <Button
              onClick={() => onSubmit(true)}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-400"
            >
              <Icon name="event_busy" />
              {submitting ? "Menyimpan…" : "Selesai Tanpa Murid"}
            </Button>
          ) : (
            <Button
              onClick={() => onSubmit(false)}
              disabled={submitting || terkunciTodo}
              title={terkunciTodo ? "Isi To-Do List dulu (masa pengawasan)" : undefined}
            >
              <Icon name={terkunciTodo ? "lock" : "check_circle"} />
              {submitting ? "Menyimpan…" : "Simpan"}
            </Button>
          )}
        </div>
      </div>

      {terkunciTodo && checked.length > 0 && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-bold text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          Simpan terkunci: isi dan simpan To-Do List di bawah dulu —{" "}
          {todoStatus.tahapTersimpan}/{tahapWajib} tahap tersimpan berisi aktivitas.
        </p>
      )}

      {/* To-Do List kelas — hanya bagi trainer dalam masa pengawasan (atau bila statusnya
          tak diketahui). Ditaruh tepat di bawah tombol Simpan supaya jelas menempel pada
          kelas yang sedang diajar. Kartu yang tak bersumber dari kelas (classIds kosong)
          menyembunyikan panel ini sendiri. */}
      {tampilTodo && (
        <TodoListPanel
          classIds={lockedSession.classIds}
          date={selectedDate}
          setToast={setToast}
          wajib={wajibTodo}
          onStatus={setTodoStatus}
        />
      )}

      <PunctualityGuide session={lockedSession.session} />

      {/* Kanban Layout */}
      <div className="grid gap-6 xl:grid-cols-2">
        {/* Left: Belum Hadir */}
        <KanbanPanel
          title="Belum Hadir"
          icon="pending_actions"
          count={pending.length}
          color="text-amber-500"
        >
          {pending.length === 0 ? (
            <div className="rounded-xl border border-dashed border-green-300 p-8 text-center text-green-600 dark:border-green-700/50 dark:text-green-400">
              <Icon name="celebration" className="block text-4xl mb-2" />
              Semua murid sudah hadir!
            </div>
          ) : (
            renderPending(pending)
          )}
        </KanbanPanel>

        {/* Right: Sudah Hadir (keterangan kedatangan bisa dikoreksi) */}
        <KanbanPanel
          title="Sudah Hadir"
          icon="task_alt"
          count={checked.length}
          color="text-green-500"
        >
          {checked.length === 0 && additions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-400 dark:border-slate-700">
              Belum ada murid yang hadir.
            </div>
          ) : (
            <>
              {renderChecked(checked)}

              {additions.length > 0 && (
                <>
                  <p className="pt-2 text-[11px] font-black uppercase tracking-wide text-purple-500">
                    Murid Tambahan (dari kelas lain)
                  </p>
                  {additions.map((a) => (
                    <AdditionRow
                      key={a.additionId}
                      addition={a}
                      onCancel={onCancelAddition}
                      onEditPunctuality={onEditAdditionPunctuality}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </KanbanPanel>
      </div>

      {diKelasLain.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
          <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">
            Hadir di kelas lain ({diKelasLain.length})
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Murid ini juga terdaftar di kelas lain pada jam yang sama dan hari ini sudah diabsen
            di sana. Tidak dihitung di kelas ini.
          </p>
          <ul className="mt-3 space-y-2">
            {diKelasLain.map((m) => (
              <li
                key={m.studentId}
                className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 text-sm text-slate-500 dark:bg-slate-900/50 dark:text-slate-400"
              >
                <span className="truncate font-bold">{m.name}</span>
                <span className="inline-flex shrink-0 items-center gap-1 text-xs">
                  <Icon name="lock" className="text-sm" />
                  Hadir di kelas {m.oleh || "lain"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function AdditionRow({ addition, onCancel, onEditPunctuality }) {
  const pending = addition.additionStatus === "pending";
  return (
    <div className={cx(BARIS_MURID, "rounded-xl border border-purple-200 bg-purple-50/40 p-4 dark:border-purple-800/40 dark:bg-purple-900/10")}>
      <div className="min-w-0">
        <p className="truncate font-black text-sm">{addition.name}</p>
        <p className="truncate text-xs text-slate-500">
          {addition.studentId} · dari {addition.originTrainer || "-"}
        </p>
        <span
          className={cx(
            "mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-black",
            pending
              ? "bg-amber-500/15 text-amber-600"
              : "bg-green-500/15 text-green-600"
          )}
        >
          {pending ? "Menunggu Approval" : "Hadir"}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <PunctualityPicker
          label={`Keterangan ${addition.name}`}
          value={addition.punctuality}
          onChange={(k) => onEditPunctuality(addition, k)}
        />
        <button
          onClick={() => onCancel(addition)}
          title={pending ? "Batalkan penambahan" : "Undo murid tambahan (sudah disetujui)"}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-500 dark:hover:bg-slate-700 transition"
        >
          <Icon name={pending ? "close" : "undo"} className="text-base" />
        </button>
      </div>
    </div>
  );
}

function AddStudentModal({ open, students, session, existingIds, onClose, onSubmit }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  // Wajib dipilih sebelum dikirim — sama seperti murid kelas, tak ada pilihan bawaan.
  const [punctuality, setPunctuality] = useState("");

  useEffect(() => {
    if (!open) {
      setQ("");
      setSelected(null);
      setReason("");
      setPunctuality("");
    }
  }, [open]);

  if (!open) return null;

  const inClass = existingIds || new Set();
  const query = q.trim().toLowerCase();
  const matches = query
    ? students
        .filter((s) => `${s.name} ${s.studentId}`.toLowerCase().includes(query))
        // Sembunyikan murid yang sudah terdaftar / sudah ditambahkan di sesi ini
        .filter((s) => !inClass.has(String(s.studentId)))
        .slice(0, 30)
    : [];

  return (
    <ModalOverlay>
      <div className="w-full max-w-lg space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black">Tambah Murid dari Kelas Lain</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Sesi {session} · perlu persetujuan admin
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <Icon name="close" />
          </button>
        </div>

        {!selected ? (
          <>
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama atau ID murid…"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {query === "" ? (
                <p className="py-6 text-center text-sm text-slate-400">
                  Ketik untuk mencari murid.
                </p>
              ) : matches.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">
                  Tidak ada murid cocok.
                </p>
              ) : (
                matches.map((s) => (
                  <button
                    key={s.studentId}
                    onClick={() => setSelected(s)}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left hover:border-primary hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black">{s.name}</p>
                      <p className="truncate text-xs text-slate-500">
                        {s.studentId} · {s.trainer || "-"} · {s.sessions || "-"}
                      </p>
                    </div>
                    <Icon name="add" className="text-primary" />
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 dark:border-purple-700/40 dark:bg-purple-900/10">
              <p className="font-black">{selected.name}</p>
              <p className="text-xs text-slate-500">
                {selected.studentId} · dari {selected.trainer || "-"} · {selected.sessions || "-"}
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                Keterangan kedatangan
              </p>
              <PunctualityPicker
                label="Keterangan kedatangan"
                value={punctuality}
                onChange={setPunctuality}
                className="w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Alasan (opsional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="mis. kelas pengganti"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelected(null);
                  setPunctuality("");
                }}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:border-primary hover:text-primary dark:border-slate-700 dark:text-slate-300"
              >
                ← Pilih lain
              </button>
              <Button
                className="flex-1"
                disabled={!punctuality}
                title={punctuality ? undefined : "Pilih keterangan kedatangan dulu"}
                onClick={() => onSubmit(selected.studentId, reason, punctuality)}
              >
                <Icon name="send" />
                Kirim ke Admin
              </Button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

function KanbanPanel({ title, icon, count, color, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900/50">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-800/50 rounded-t-2xl">
        <Icon name={icon} className={cx("text-base", color)} />
        <span className="font-black">{title}</span>
        <span className={cx("ml-auto rounded-full px-2.5 py-0.5 text-xs font-black", color, "bg-current/10")}>
          {count}
        </span>
      </div>
      <div className="space-y-3 p-4">{children}</div>
    </div>
  );
}

// Baris murid: nama di atas tombol pada layar sempit, sebaris bila panelnya cukup lebar.
// Di xl kedua panel tampil berdampingan (masing-masing setengah lebar), jadi bertumpuk lagi
// sampai 2xl — kalau tidak, namanya terpotong oleh tiga tombol.
const BARIS_MURID =
  "flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between xl:flex-col xl:items-stretch 2xl:flex-row 2xl:items-center";

function PendingRow({ student, onMark }) {
  return (
    <div className={cx(
      BARIS_MURID,
      "rounded-xl border p-3",
      student.isCovered
        ? "border-purple-200 bg-purple-50/50 dark:border-purple-700/40 dark:bg-purple-900/10"
        : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50"
    )}>
      <div className="min-w-0">
        <p className="truncate font-black text-sm">{student.name}</p>
        <p className="truncate text-xs text-slate-500">{student.studentId} · {student.sessions}</p>
        {(student.isRescheduled || student.isCovered) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {student.isRescheduled && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-black text-amber-600">
                Reschedule
              </span>
            )}
            {student.isCovered && (
              <span className="rounded-full bg-purple-500/15 px-2 py-0.5 text-[10px] font-black text-purple-600">
                Covered · dari {student.trainer}
              </span>
            )}
          </div>
        )}
      </div>
      {/* Satu ketukan = hadir + keterangannya. Ketiganya sama bobotnya: tak ada pilihan
          bawaan yang bisa ditekan tanpa berpikir. */}
      <div
        role="group"
        aria-label={`Tandai hadir ${student.name}`}
        className="grid shrink-0 grid-cols-3 gap-1.5 sm:w-64 xl:w-auto 2xl:w-64"
      >
        {PUNCTUALITY.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => onMark(student, k)}
            className={cx(
              "whitespace-nowrap rounded-lg border px-2 py-2 text-xs font-black transition",
              PUNCTUALITY_STYLES[k].pick
            )}
          >
            {PUNCTUALITY_STYLES[k].label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Pemilih keterangan kedatangan — untuk murid yang sudah hadir, murid tambahan, dan jendela
// Tambah Murid. `value` kosong (absensi lama tanpa jam) = belum ada yang terpilih.
function PunctualityPicker({ label, value, onChange, className = "" }) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cx(
        "inline-grid grid-cols-3 gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900",
        className
      )}
    >
      {PUNCTUALITY.map((k) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          onClick={() => value !== k && onChange(k)}
          className={cx(
            "whitespace-nowrap rounded-md px-2.5 py-1.5 text-[11px] font-black transition-colors",
            value === k
              ? PUNCTUALITY_STYLES[k].on
              : "text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          )}
        >
          {PUNCTUALITY_STYLES[k].label}
        </button>
      ))}
    </div>
  );
}

// Panduan keterangan, dihitung dari jam mulai kelas di kartu ini. Hanya panduan: trainer yang
// menilai — jam tombol ditekan bukan jam murid datang, jadi tak ada yang dicocokkan otomatis.
function PunctualityGuide({ session }) {
  const g = punctualityGuide(session);
  const bagian = g
    ? [["ontime", `sampai ${g.ontimeUntil}`], ["late", `${g.lateFrom}–${g.lateUntil}`], ["verylate", `setelah ${g.lateUntil}`]]
    : [["ontime", `≤ ${ONTIME_MAX_MIN} menit`], ["late", `${ONTIME_MAX_MIN + 1}–${LATE_MAX_MIN} menit`], ["verylate", `> ${LATE_MAX_MIN} menit`]];
  return (
    <section
      aria-label="Panduan keterangan kedatangan"
      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300"
    >
      <span className="font-black uppercase tracking-wide text-slate-500">
        {g ? `Kelas mulai ${g.start}` : "Dari jam mulai kelas"}
      </span>
      {bagian.map(([k, teks]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={cx("h-2 w-2 rounded-full", PUNCTUALITY_STYLES[k].dot)} />
          <b>{PUNCTUALITY_STYLES[k].label}</b> {teks}
        </span>
      ))}
    </section>
  );
}

function CheckedRow({ student, onUndo, onEditPunctuality }) {
  const coverInfo = String(student.status || "").split(":")[1]; // nama trainer cover jika ada

  return (
    <div className={cx(BARIS_MURID, "rounded-xl border border-green-200 bg-green-50/40 p-4 dark:border-green-800/40 dark:bg-green-900/10")}>
      <div className="min-w-0">
        <p className="truncate font-black text-sm">{student.name}</p>
        <p className="truncate text-xs text-slate-500">{student.studentId} · {student.sessions}</p>
        {student.isCovered && (
          <p className="mt-0.5 text-[10px] font-bold text-purple-500">
            Covered · dari {student.trainer}
          </p>
        )}
        {coverInfo && (
          <p className="mt-0.5 text-[10px] font-bold text-blue-500">
            Dicatat oleh: {coverInfo}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <PunctualityPicker
          label={`Keterangan ${student.name}`}
          value={student.punctuality}
          onChange={(k) => onEditPunctuality(student, k)}
        />
        <button
          onClick={() => onUndo(student)}
          title="Batalkan kehadiran"
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 transition"
        >
          <Icon name="undo" className="text-base" />
        </button>
      </div>
    </div>
  );
}
